import { Injectable, inject } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MarketingOrder, AgentType } from '../models';
import { ApiService } from './api.service';
import { UpdateCurrentOrderState } from '../state-mgmt';

/**
 * @deprecated
 */
@Injectable({
  providedIn: 'root',
})
export class MarketingOrderAgentService {
  private readonly apiService = inject(ApiService);
  private readonly store = inject<Store<any>>(Store);

  /**
   * Updates the Listing to singleAgent and dispatches an update to the store for the current marketing order
   * */
  async marketAsSingleAgent(marketingOrder: MarketingOrder): Promise<MarketingOrder> {
    this.setAgentType(marketingOrder, AgentType.singleAgent);
    return await this.updateListingAgent(marketingOrder);
  }

  /**
   * Updates the Listing to twoAgents and dispatches an update to the store for the current marketing order
   * */
  async marketAsCoList(marketingOrder: MarketingOrder, coListAgentId: string, coListAgentProfileId: string): Promise<MarketingOrder> {
    this.setAgentType(marketingOrder, AgentType.coListing);
    marketingOrder.coListAgentId = coListAgentId;
    if (coListAgentProfileId) {
      marketingOrder.coListAgentProfileId = coListAgentProfileId;
    }
    return await this.updateListingAgent(marketingOrder);
  }

  /**
   * Updates the Listing to team and dispatches an update to the store for the current marketing order
   * */
  async marketAsTeam(marketingOrder: MarketingOrder, teamId: string): Promise<MarketingOrder> {
    this.setAgentType(marketingOrder, AgentType.team);
    marketingOrder.teamId = teamId;
    return await this.updateListingAgent(marketingOrder);
  }

  private async updateListingAgent(marketingOrder: MarketingOrder) {
    const payload: { agentType: AgentType, coListAgentId: string, teamId: string, coListAgentProfileId: string } = {
      agentType: marketingOrder.listing.agentType,
      coListAgentId: marketingOrder.coListAgentId,
      coListAgentProfileId: marketingOrder.coListAgentProfileId,
      teamId: marketingOrder.teamId,
    };
    return await this.apiService.put(`marketing-orders/${marketingOrder._id}/listing-agent`, payload).pipe(
      map((response) => new MarketingOrder(response)),
      tap((updatedOrder) => this.store.dispatch(UpdateCurrentOrderState({ payload: updatedOrder }))),
    ).toPromise();
  }

  /**
   * Updates the listing with the appropriate agent type and clears out any values
   * that no longer pertain
   * */
  private setAgentType(marketingOrder: MarketingOrder, agentType: AgentType) {
    marketingOrder.listing.agentType = agentType;
    if (marketingOrder.listing.agentType === AgentType.singleAgent) {
      // If single agent, clear out coList and team ids
      marketingOrder.coListAgentId = null;
      marketingOrder.teamId = null;
    } else if (marketingOrder.listing.agentType === AgentType.coListing) {
      // If coList, clear out teamId
      marketingOrder.teamId = null;
    } else if (marketingOrder.listing.agentType === AgentType.team) {
      // If team, clear out coList id
      marketingOrder.coListAgentId = null;
    }
  }
}
